import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/ui/markdown";
import { Seo } from "@/components/Seo";
import { ProjectIcon } from "@/components/ProjectIcon";
import type { Domain, ProjectWithDomains } from "@/lib/types";

interface DomainWithProjects extends Domain {
  projects: ProjectWithDomains[];
}

export function DomainDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [data, setData] = useState<DomainWithProjects | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    fetch(`/api/domains/${slug}`)
      .then((r) => {
        if (!r.ok) { setNotFound(true); return null; }
        return r.json();
      })
      .then((d) => d && setData(d));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container mx-auto px-6 py-16 text-center">
        <Seo title="Domain Not Found" description="This domain couldn't be found." path={`/domains/${slug}`} noindex />
        <div className="text-6xl mb-4">🦆💨</div>
        <h1 className="font-display text-3xl font-bold tracking-tight mb-4">Domain Not Found</h1>
        <Button asChild variant="outline"><Link to="/domains">Back to Domains</Link></Button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="container mx-auto px-6 py-16 text-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-16 max-w-5xl">
      <Seo
        title={data.name}
        description={data.tagline || `${data.name} at Loose Goose Systems.`}
        path={`/domains/${data.slug}`}
      />
      <Link to="/domains" className="text-sm text-muted-foreground hover:text-foreground mb-8 inline-flex items-center gap-1 transition-colors">
        &larr; All Domains
      </Link>

      <div className="mt-6 mb-12 max-w-3xl">
        <div className="text-6xl mb-4">{data.icon || "🦆"}</div>
        <h1 className="font-display text-4xl font-extrabold tracking-tight mb-2">{data.name}</h1>
        <p className="text-xl text-muted-foreground mb-6">{data.tagline}</p>
        {data.description && <Markdown source={data.description} />}
      </div>

      <h2 className="font-display text-2xl font-bold tracking-tight mb-6">Projects in this domain</h2>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {data.projects.map((project) => (
          <Link key={project.slug} to={`/projects/${project.slug}`} className="group">
            <Card className="h-full transition-all duration-300 group-hover:-translate-y-1 group-hover:border-teal/60 group-hover:shadow-lg">
              <CardHeader>
                <ProjectIcon icon={project.icon_url} size="text-4xl" className="mb-2" alt={project.name} />
                <CardTitle className="font-display text-xl transition-colors group-hover:text-teal">
                  {project.name}
                </CardTitle>
                <CardDescription className="text-base">{project.tagline}</CardDescription>
              </CardHeader>
              {project.domains.length > 1 && (
                <CardContent className="flex flex-wrap gap-1.5">
                  {project.domains
                    .filter((d) => d.slug !== data.slug)
                    .map((d) => (
                      <Badge key={d.id} domainSlug={d.slug}>
                        {d.icon && <span className="mr-1">{d.icon}</span>}
                        {d.name}
                      </Badge>
                    ))}
                </CardContent>
              )}
            </Card>
          </Link>
        ))}
      </div>
      {data.projects.length === 0 && (
        <p className="text-muted-foreground">No projects in this domain yet — stay tuned.</p>
      )}
    </div>
  );
}
